import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Tabs,
  Form,
  Select,
  DatePicker,
  InputNumber,
  message,
  Tooltip,
  Input,
  Modal,
  Descriptions,
  Image,
  Divider,
  Popconfirm,
} from 'antd';
import {
  FileTextOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  SearchOutlined,
  ClearOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  CommentOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Api from '../../Network';
import MobileTable from '../../Component/MobileTable';
import { Visit, VisitFilters, VisitsResponse } from '../../types/types';
import { usePermissions } from '../../contexts/PermissionContext';
import FollowUpRequests from './FollowUpRequests';
import { AnimatedContainer, PageHeader, StatCard, SectionCard } from '../../Component/ui';
import './style.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea: AntTextArea } = Input;

interface VisitsState {
  visits: Visit[];
  loading: boolean;
  tabLoading: boolean;
  stats: {
    total: number;
    pending: number;
    completed: number;
    cancelled: number;
  };
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
}

const buildVisitsQueryString = (queryParams: VisitFilters): string => {
  return Object.entries(queryParams)
    .filter(([_, value]) => value !== undefined && value !== '' && value !== null)
    .map(([key, value]) => {
      if (key === 'global' && value) {
        return `filter[global]=${encodeURIComponent(String(value))}`;
      }
      if (key === 'status' && value) {
        return `filter[status]=${encodeURIComponent(String(value))}`;
      }
      if (key === 'from' && value) {
        return `filter[from]=${encodeURIComponent(String(value))}`;
      }
      if (key === 'to' && value) {
        return `filter[to]=${encodeURIComponent(String(value))}`;
      }
      if (key === 'driver_id' && value) {
        return `filter[driver_id]=${encodeURIComponent(String(value))}`;
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
    })
    .join('&');
};

const Visits: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [form] = Form.useForm();
  const [noteForm] = Form.useForm();

  const [visitsState, setVisitsState] = useState<VisitsState>({
    visits: [],
    loading: false,
    tabLoading: false,
    stats: { total: 0, pending: 0, completed: 0, cancelled: 0 },
    pagination: { current: 1, pageSize: 25, total: 0 },
  });

  const [filters, setFilters] = useState<VisitFilters>({
    page: 1,
    'per-page': 25,
    from: '',
    to: '',
    global: '',
    // status: '',
    driver_id: undefined,
  });

  const [activeTab, setActiveTab] = useState<string>('all');
  const [actionsDays, setActionsDays] = useState<number>(21);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [visitDetailsVisible, setVisitDetailsVisible] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [driversList, setDriversList] = useState<{ id: number; name: string }[]>([]);
  const [pendingReassignDriverId, setPendingReassignDriverId] = useState<number | undefined>(
    undefined
  );
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response: any = await Api.get('/api/admin/drivers', {
          headers: { 'per-page': '1000' },
        });
        if (cancelled) {
          return;
        }
        if (response.status && response.data?.data) {
          setDriversList(
            response.data.data.map((d: { id: number; name: string }) => ({
              id: d.id,
              name: d.name,
            }))
          );
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (filtersVisible) {
      form.setFieldsValue({
        global: filters.global,
        driver_id: filters.driver_id,
      });
    }
  }, [filtersVisible, filters.global, filters.driver_id, form]);

  useEffect(() => {
    if (!visitDetailsVisible) {
      setPendingReassignDriverId(undefined);
    }
  }, [visitDetailsVisible]);

  // Fetch visits data
  const fetchVisits = async (customFilters?: VisitFilters) => {
    setVisitsState((prev) => ({ ...prev, loading: true }));

    try {
      const queryParams = customFilters || filters;
      const queryString = buildVisitsQueryString(queryParams);

      const response: any = await Api.get(`/api/admin/visits?${queryString}`);

      if (response.status && response.data) {
        const visitsData: VisitsResponse = response.data;

        // Calculate stats
        const stats = {
          total: visitsData.total,
          pending: visitsData.data.filter((visit: Visit) => visit.status === 'pending').length,
          completed: visitsData.data.filter((visit: Visit) => visit.status === 'completed').length,
          cancelled: visitsData.data.filter((visit: Visit) => visit.status === 'cancelled').length,
        };

        setVisitsState((prev) => ({
          ...prev,
          visits: visitsData.data,
          loading: false,
          stats,
          pagination: {
            current: visitsData.current_page,
            pageSize: visitsData.per_page,
            total: visitsData.total,
          },
        }));
      } else {
        message.error(response.message || 'Failed to fetch visits');
        setVisitsState((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      message.error('Failed to fetch visits');
      setVisitsState((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchVisits();
  }, []);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setVisitsState((prev) => ({ ...prev, tabLoading: true }));

    const newFilters = { ...filters };
    switch (key) {
      case 'actions':
        delete newFilters.status;
        newFilters.actions = 1;
        newFilters.days = actionsDays;
        newFilters.page = 1;
        break;
      case 'pending':
        newFilters.status = 'pending';
        delete newFilters.actions;
        delete newFilters.days;
        break;
      case 'completed':
        newFilters.status = 'completed';
        delete newFilters.actions;
        delete newFilters.days;
        break;
      case 'cancelled':
        newFilters.status = 'cancelled';
        delete newFilters.actions;
        delete newFilters.days;
        break;
      default:
        delete newFilters.status;
        delete newFilters.actions;
        delete newFilters.days;
    }

    setFilters(newFilters);
    fetchVisits(newFilters).finally(() => {
      setVisitsState((prev) => ({ ...prev, tabLoading: false }));
    });
  };

  const handleFilterSubmit = (values: any) => {
    const newFilters = {
      ...filters,
      ...values,
      page: 1,
    };

    if (values.date_range && values.date_range.length === 2) {
      newFilters.from = values.date_range[0].format('YYYY-MM-DD');
      newFilters.to = values.date_range[1].format('YYYY-MM-DD');
      delete newFilters.date_range;
    }

    setFilters(newFilters);
    fetchVisits(newFilters);
    setFiltersVisible(false);
  };

  const handleFilterClear = () => {
    const clearedFilters = {
      page: 1,
      'per-page': 25,
      from: '',
      to: '',
      global: '',
      status: '',
      driver_id: undefined,
      actions: undefined,
      days: undefined,
    };
    setFilters(clearedFilters);
    form.resetFields();
    fetchVisits(clearedFilters);
    setFiltersVisible(false);
  };

  const handleViewVisit = (visit: Visit) => {
    // Check permission before showing visit details (view_visit is for details, view_visits is for list)
    const canViewVisit =
      hasPermission('view_visit') || hasPermission('visits.read') || hasPermission('visits.view');

    if (!canViewVisit) {
      message.warning(
        t('permissions.accessDenied') || 'You do not have permission to view visit details'
      );
      return;
    }

    setSelectedVisit(visit);
    setVisitDetailsVisible(true);
  };

  const handleCloseVisitDetails = () => {
    setVisitDetailsVisible(false);
    setSelectedVisit(null);
  };

  const handleTableChange = (pagination: any, _tableFilters: any, _sorter: any) => {
    const newFilters = {
      ...filters,
      page: pagination.current,
      'per-page': pagination.pageSize,
    };
    setFilters(newFilters);
    fetchVisits(newFilters);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'orange';
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'canceled':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return t('visits.status.pending');
      case 'completed':
        return t('visits.status.completed');
      case 'cancelled':
      case 'canceled':
        return t('visits.status.cancelled');
      default:
        return status;
    }
  };

  // Admin Actions
  const handleReassignDriver = async (visitId: number, driverId: number) => {
    setActionLoading(true);
    try {
      const response: any = await Api.post(`/api/admin/visits/${visitId}/reassign-driver`, {
        driver_id: driverId,
      });

      if (response.status && response.data) {
        message.success(
          t('visits.reassignDriver.success', 'Driver updated and notification sent successfully')
        );
        setSelectedVisit(response.data);
        setPendingReassignDriverId(undefined);
        fetchVisits();
      } else {
        message.error(
          response.msg || response.message || t('visits.reassignDriver.error', 'Failed to reassign')
        );
      }
    } catch (error) {
      message.error(t('visits.reassignDriver.error', 'Failed to reassign'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleVisitRequestAction = async (
    visitRequestId: number,
    status: 'approved' | 'rejected'
  ) => {
    setActionLoading(true);
    try {
      const response: any = await Api.post('/api/admin/visit-requests', {
        visit_request_id: visitRequestId,
        status: status,
      });

      if (response.status) {
        message.success(`Visit request ${status} successfully`);

        // Update the visit request status in selectedVisit without reloading
        if (selectedVisit && selectedVisit.visit_requests) {
          const updatedVisitRequests = selectedVisit.visit_requests.map((vr) =>
            vr.id === visitRequestId ? { ...vr, status: status } : vr
          );
          setSelectedVisit({
            ...selectedVisit,
            visit_requests: updatedVisitRequests,
          });
        }

        // Refresh the visits list in the background (without showing loading)
        fetchVisits();
      } else {
        message.error(response.message || `Failed to ${status} visit request`);
      }
    } catch (error) {
      message.error(`Failed to ${status} visit request`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddFollowUpNote = async (followUpId: number, note: string) => {
    setActionLoading(true);
    try {
      const response: any = await Api.post(`/api/admin/followup/${followUpId}`, {
        note: note,
      });

      if (response.status) {
        message.success('Note added successfully');
        setNoteModalVisible(false);
        setSelectedFollowUp(null);
        noteForm.resetFields();
        // Refresh the visit details
        // if (selectedVisit) {
        //   const updatedVisit: any = await Api.get(`/api/admin/visits/${selectedVisit.id}`);
        //   if (updatedVisit.status && updatedVisit.data) {
        //     setSelectedVisit(updatedVisit.data);
        //   }
        // }
        fetchVisits();
      } else {
        message.error(response.message || 'Failed to add note');
      }
    } catch (error) {
      message.error('Failed to add note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeVisitStatus = async (visitId: number, status: string) => {
    setActionLoading(true);
    try {
      const response: any = await Api.post(`/api/admin/visits/status/${visitId}`, {
        status: status,
      });

      if (response.status) {
        message.success(`Visit status updated to ${status} successfully`);
        // Refresh the visit details
        // if (selectedVisit) {
        //   const updatedVisit: any = await Api.get(`/api/admin/visits/${selectedVisit.id}`);
        //   if (updatedVisit.status && updatedVisit.data) {
        //     setSelectedVisit(updatedVisit.data);
        //   }
        // }
        // Refresh the visits list
        fetchVisits();
      } else {
        message.error(response.message || 'Failed to update visit status');
      }
    } catch (error) {
      message.error('Failed to update visit status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenNoteModal = (followUp: any) => {
    setSelectedFollowUp(followUp);
    setNoteModalVisible(true);
  };

  const handleCloseNoteModal = () => {
    setNoteModalVisible(false);
    setSelectedFollowUp(null);
    noteForm.resetFields();
  };

  // Download all filtered results (not just the current page)
  const handleDownloadResults = async () => {
    const totalResults = visitsState.pagination.total;

    if (totalResults === 0) {
      message.warning(t('visits.download.noData'));
      return;
    }

    setDownloading(true);

    try {
      const exportFilters: VisitFilters = {
        ...filters,
        page: 1,
        'per-page': totalResults,
      };
      const queryString = buildVisitsQueryString(exportFilters);
      const response: any = await Api.get(`/api/admin/visits?${queryString}`);

      if (!response.status || !response.data?.data?.length) {
        message.warning(t('visits.download.noData'));
        return;
      }

      const allVisits: Visit[] = response.data.data;

      // Prepare CSV data
      const csvData = allVisits.map((visit) => ({
        [t('visits.columns.id')]: visit.id,
        [t('visits.columns.businessName')]: visit.business_name || '',
        [t('visits.columns.tradeName')]: visit.trade_name || '',
        [t('visits.columns.customerName')]: visit.customer_name || '',
        [t('visits.columns.customerPhone')]: visit.customer_phone || '',
        [t('visits.columns.location')]: visit.customer_location || '',
        [t('visits.columns.driver')]: visit.driver?.name || '',
        [t('visits.columns.status')]: getStatusText(visit.status),
        [t('visits.columns.createdAt')]: new Date(visit.created_at).toLocaleDateString(),
      }));

      // Convert to CSV
      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(','),
        ...csvData.map((row: any) => headers.map((header: string) => `"${row[header]}"`).join(',')),
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `visits_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(t('visits.download.success'));
    } catch (error) {
      message.error(t('visits.download.error'));
    } finally {
      setDownloading(false);
    }
  };

  const columns: any[] = [
    {
      title: t('visits.columns.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
      'data-label': t('visits.columns.id'),
    },
    {
      title: t('visits.columns.tradeName'),
      dataIndex: 'trade_name',
      key: 'trade_name',
      sorter: true,
      'data-label': t('visits.columns.tradeName'),
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: t('visits.columns.customerName'),
      dataIndex: 'customer_name',
      key: 'customer_name',
      sorter: true,
      'data-label': t('visits.columns.customerName'),
    },
    {
      title: t('visits.columns.customerPhone'),
      dataIndex: 'customer_phone',
      key: 'customer_phone',
      'data-label': t('visits.columns.customerPhone'),
      render: (text: string) => (
        <Space>
          <PhoneOutlined />
          <Text copyable>{text}</Text>
        </Space>
      ),
    },
    {
      title: t('visits.columns.location'),
      dataIndex: 'customer_location',
      key: 'customer_location',
      'data-label': t('visits.columns.location'),
      render: (text: string) => (
        <Space>
          <EnvironmentOutlined />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: t('visits.columns.driver'),
      dataIndex: ['driver', 'name'],
      key: 'driver_name',
      'data-label': t('visits.columns.driver'),
      render: (text: string, record: Visit) => (
        <Space>
          <UserOutlined />
          <Text>{record.driver?.name || text}</Text>
        </Space>
      ),
    },
    {
      title: t('visits.columns.status'),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      'data-label': t('visits.columns.status'),
      render: (status: string) => (
        <Tag color={getStatusColor(status)} className="status-tag">
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: t('visits.columns.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      'data-label': t('visits.columns.createdAt'),
      render: (text: string) => (
        <Space>
          <CalendarOutlined />
          <Text>{new Date(text).toLocaleDateString()}</Text>
        </Space>
      ),
    },
    {
      title: t('visits.columns.lastUpdated'),
      dataIndex: 'follow_ups',
      key: 'last_followup_update',
      width: 180,
      sorter: true,
      'data-label': t('visits.columns.lastUpdated'),
      render: (followUps: any[], record: Visit) => {
        // Get the latest follow-up update
        let latestUpdate = null;

        if (followUps && followUps.length > 0) {
          // Sort follow-ups by updated_at to get the latest
          const sortedFollowUps = [...followUps].sort(
            (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
          latestUpdate = sortedFollowUps[0].updated_at;
        }

        // If no follow-ups, use visit's updated_at
        if (!latestUpdate) {
          latestUpdate = record.updated_at;
        }

        if (!latestUpdate) {
          return (
            <div style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>
              {t('common.noData', 'No Data')}
            </div>
          );
        }

        const date = new Date(latestUpdate);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

        let timeDisplay = '';
        if (diffInHours < 1) {
          timeDisplay = t('common.justNow', 'Just now');
        } else if (diffInHours < 24) {
          timeDisplay = t('common.hoursAgo', '{{hours}} hours ago', { hours: diffInHours });
        } else {
          const diffInDays = Math.floor(diffInHours / 24);
          timeDisplay = t('common.daysAgo', '{{days}} days ago', { days: diffInDays });
        }

        // Show indicator if it's from follow-up
        const isFromFollowUp = followUps && followUps.length > 0;

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
            }}
          >
            <ClockCircleOutlined
              style={{
                color: isFromFollowUp ? '#52c41a' : '#1890ff',
                fontSize: '14px',
              }}
            />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>
                {date.toLocaleDateString()}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div style={{ fontSize: '11px', color: isFromFollowUp ? '#52c41a' : '#1890ff' }}>
                {timeDisplay}
              </div>
              {isFromFollowUp && (
                <div style={{ fontSize: '10px', color: '#52c41a', fontWeight: 500 }}>
                  {t('visits.lastFollowUpUpdate', 'آخر متابعة')}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: t('visits.columns.actions'),
      key: 'actions',
      'data-label': t('visits.columns.actions'),
      render: (_: any, record: Visit) => {
        // Check if user has permission to view visit details (view_visit is for details, view_visits is for list)
        const canViewVisit =
          hasPermission('view_visit') ||
          hasPermission('visits.read') ||
          hasPermission('visits.view');

        if (!canViewVisit) {
          return null;
        }

        return (
          <Space size="middle">
            <Tooltip title={t('common.view')}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleViewVisit(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <AnimatedContainer className="visits-page" variant="fadeUp">
      {/* Header */}
      <PageHeader
        icon={<FileTextOutlined />}
        title={t('visits.title')}
        subtitle={t('visits.description')}
        actions={
          <Space>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFiltersVisible(!filtersVisible)}
              type={filtersVisible ? 'primary' : 'default'}
            >
              {t('common.filter')}
            </Button>
            {(hasPermission('download_visit_report') || hasPermission('export_visits')) && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadResults}
                disabled={visitsState.pagination.total === 0}
                loading={downloading}
              >
                {t('visits.download.button')}
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchVisits()}
              loading={visitsState.loading}
            >
              {t('common.refresh')}
            </Button>
          </Space>
        }
      />

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            tone="info"
            icon={<FileTextOutlined />}
            title={t('visits.stats.total')}
            value={visitsState.stats.total}
          />
        </Col>
        {/* <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('visits.stats.pending')}
              value={visitsState.stats.pending}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('visits.stats.completed')}
              value={visitsState.stats.completed}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('visits.stats.cancelled')}
              value={visitsState.stats.cancelled}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>*/}
      </Row>

      {/* Filters */}
      {filtersVisible && (
        <SectionCard className="premium-filter-form" style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical" onFinish={handleFilterSubmit} initialValues={filters}>
            <Row gutter={16} align="bottom">
              <Col xs={24} sm={12} lg={6}>
                <Form.Item name="global" label={t('visits.filters.search')}>
                  <Input
                    placeholder={t('visits.filters.searchPlaceholder')}
                    allowClear
                    prefix={<SearchOutlined style={{ color: 'var(--color-text-tertiary)' }} />}
                  />
                </Form.Item>
              </Col>
              {/* <Col xs={24} sm={12} lg={6}>
                <Form.Item name="status" label={t('visits.filters.status')}>
                  <Select placeholder={t('visits.filters.selectStatus')} allowClear>
                    <Option value="pending">{t('visits.status.pending')}</Option>
                    <Option value="completed">{t('visits.status.completed')}</Option>
                    <Option value="cancelled">{t('visits.status.cancelled')}</Option>
                  </Select>
                </Form.Item>
              </Col> */}
              <Col xs={24} sm={12} lg={6}>
                <Form.Item name="date_range" label={t('visits.filters.dateRange')}>
                  <RangePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item name="driver_id" label={t('visits.filters.activeDriver')}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('visits.filters.selectActiveDriver')}
                    options={driversList.map((d) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6} className="premium-filter-form__actions-col">
                <Form.Item className="premium-filter-form__actions" label=" ">
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                      {t('common.search')}
                    </Button>
                    <Button
                      type="default"
                      htmlType="button"
                      onClick={handleFilterClear}
                      icon={<ClearOutlined />}
                    >
                      {t('common.clear')}
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </SectionCard>
      )}

      {/* Tabs and Table */}
      <SectionCard>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab={t('visits.tabs.all', 'All Visits')} key="all" />
          <TabPane
            tab={t('visits.tabs.followUpRequests', 'Follow-up Requests')}
            key="followUpRequests"
          />
          <TabPane tab={t('visits.tabs.actions', 'اجراءات تحتاج الى متابعة')} key="actions" />
          {/* <TabPane tab={t('visits.tabs.pending')} key="pending" />
          <TabPane tab={t('visits.tabs.completed')} key="completed" />
          <TabPane tab={t('visits.tabs.cancelled')} key="cancelled" /> */}
        </Tabs>

        {activeTab === 'all' || activeTab === 'actions' ? (
          <>
            {activeTab === 'actions' && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <Text>
                  {t(
                    'visits.actionsTab.description',
                    'Show visits with no follow-up activity for at least N days'
                  )}
                </Text>
                <Space>
                  <Text>{t('visits.actionsTab.daysLabel', 'Days')}</Text>
                  <InputNumber
                    min={1}
                    max={365}
                    value={actionsDays}
                    onChange={(value) => setActionsDays(Number(value || 21))}
                  />
                  <Button
                    type="primary"
                    onClick={() => {
                      const newFilters = {
                        ...filters,
                        actions: 1,
                        days: actionsDays,
                        page: 1,
                      };
                      setFilters(newFilters);
                      fetchVisits(newFilters);
                    }}
                  >
                    {t('common.apply', 'Apply')}
                  </Button>
                </Space>
              </div>
            )}

            <MobileTable
              columns={columns}
              dataSource={visitsState.visits}
              rowKey="id"
              loading={visitsState.loading || visitsState.tabLoading}
              pagination={{
                current: visitsState.pagination.current,
                pageSize: visitsState.pagination.pageSize,
                total: visitsState.pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total: number, range: [number, number]) =>
                  `${t('common.showing')} ${range[0]}-${range[1]} ${t('common.of')} ${total} ${t('visits.items')}`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
            />
          </>
        ) : activeTab === 'followUpRequests' ? (
          <FollowUpRequests onRefresh={() => fetchVisits()} />
        ) : null}
      </SectionCard>

      {/* Visit Details Modal */}
      <Modal
        title={t('visits.details.title')}
        visible={visitDetailsVisible}
        onCancel={handleCloseVisitDetails}
        footer={[
          <Button key="close" onClick={handleCloseVisitDetails}>
            {t('common.close')}
          </Button>,
        ]}
        width={1000}
      >
        {selectedVisit && (
          <div>
            <Descriptions title={t('visits.details.basicInfo')} bordered column={2}>
              <Descriptions.Item label={t('visits.columns.id')}>
                {selectedVisit.id}
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.columns.businessName')}>
                {selectedVisit.business_name}
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.columns.tradeName')}>
                {selectedVisit.trade_name}
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.columns.customerName')}>
                {selectedVisit.customer_name}
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.columns.customerPhone')}>
                {selectedVisit.customer_phone}
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.columns.location')}>
                {selectedVisit.customer_location}
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.columns.driver')} span={2}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>{selectedVisit.driver?.name || '—'}</Text>
                  {(hasPermission('approve_visit') ||
                    hasPermission('update_visit') ||
                    hasPermission('visits.approve')) && (
                    <Space wrap>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder={t('visits.details.selectNewDriver')}
                        style={{ minWidth: 240 }}
                        value={pendingReassignDriverId}
                        onChange={(v) => setPendingReassignDriverId(v)}
                        allowClear
                        options={driversList.map((d) => ({
                          value: d.id,
                          label: d.name,
                        }))}
                      />
                      <Popconfirm
                        title={t('visits.details.confirmReassignDriver')}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                        disabled={
                          !pendingReassignDriverId ||
                          pendingReassignDriverId === selectedVisit.driver_id
                        }
                        onConfirm={() => {
                          if (pendingReassignDriverId) {
                            handleReassignDriver(selectedVisit.id, pendingReassignDriverId);
                          }
                        }}
                      >
                        <Button
                          type="primary"
                          loading={actionLoading}
                          disabled={
                            !pendingReassignDriverId ||
                            pendingReassignDriverId === selectedVisit.driver_id
                          }
                        >
                          {t('visits.details.applyDriverChange')}
                        </Button>
                      </Popconfirm>
                    </Space>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.columns.status')}>
                <Space>
                  <Tag color={getStatusColor(selectedVisit.status)}>
                    {getStatusText(selectedVisit.status)}
                  </Tag>
                  {(hasPermission('update_visit') ||
                    hasPermission('change_visit_status') ||
                    hasPermission('visits.update')) && (
                    <Select
                      defaultValue={selectedVisit.status}
                      style={{ width: 120 }}
                      onChange={(value) => handleChangeVisitStatus(selectedVisit.id, value)}
                      loading={actionLoading}
                    >
                      <Option value="pending">{t('visits.status.pending')}</Option>
                      <Option value="completed">{t('visits.status.completed')}</Option>
                      <Option value="canceled">{t('visits.status.cancelled')}</Option>
                    </Select>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.columns.createdAt')}>
                {new Date(selectedVisit.created_at).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            {/* Visit Requests */}
            {selectedVisit.visit_requests && selectedVisit.visit_requests.length > 0 && (
              <>
                <Divider>{t('visits.details.visitRequests')}</Divider>
                {selectedVisit.visit_requests.map((visitRequest) => (
                  <Card key={visitRequest.id} style={{ marginBottom: 16 }}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label={t('visits.details.requestStatus')}>
                        <Space>
                          <Tag color={getStatusColor(visitRequest.status)}>
                            {getStatusText(visitRequest.status)}
                          </Tag>
                          {visitRequest.status === 'pending' &&
                            (hasPermission('approve_visit') ||
                              hasPermission('update_visit') ||
                              hasPermission('visits.approve')) && (
                              <Space>
                                {(hasPermission('approve_visit') ||
                                  hasPermission('update_visit') ||
                                  hasPermission('visits.approve')) && (
                                  <Popconfirm
                                    title={t('visits.actions.confirmApprove')}
                                    onConfirm={() =>
                                      handleVisitRequestAction(visitRequest.id, 'approved')
                                    }
                                    okText={t('common.yes')}
                                    cancelText={t('common.no')}
                                  >
                                    <Button
                                      type="primary"
                                      size="small"
                                      icon={<CheckOutlined />}
                                      loading={actionLoading}
                                    >
                                      {t('visits.actions.approve')}
                                    </Button>
                                  </Popconfirm>
                                )}
                                {(hasPermission('reject_visit') ||
                                  hasPermission('update_visit') ||
                                  hasPermission('visits.reject')) && (
                                  <Popconfirm
                                    title={t('visits.actions.confirmReject')}
                                    onConfirm={() =>
                                      handleVisitRequestAction(visitRequest.id, 'rejected')
                                    }
                                    okText={t('common.yes')}
                                    cancelText={t('common.no')}
                                  >
                                    <Button
                                      danger
                                      size="small"
                                      icon={<CloseOutlined />}
                                      loading={actionLoading}
                                    >
                                      {t('visits.actions.reject')}
                                    </Button>
                                  </Popconfirm>
                                )}
                              </Space>
                            )}
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('visits.details.requestedBy')}>
                        {visitRequest.driver?.name || `Driver ${visitRequest.requested_by}`}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('visits.details.requestedAt')}>
                        {new Date(visitRequest.created_at).toLocaleString()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ))}
              </>
            )}

            {/* Follow-ups */}
            {selectedVisit.follow_ups && selectedVisit.follow_ups.length > 0 && (
              <>
                <Divider>{t('visits.details.followUps')}</Divider>
                {selectedVisit.follow_ups.map((followUp) => (
                  <Card key={followUp.id} style={{ marginBottom: 16 }}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label={t('visits.columns.driver')}>
                        <Space>
                          <UserOutlined />
                          <Text strong>
                            {followUp.driver?.name ?? `— (#${followUp.driver_id})`}
                          </Text>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('visits.details.report')}>
                        {followUp.report}
                      </Descriptions.Item>
                      {followUp.image_path && (
                        <Descriptions.Item label={t('visits.details.image')}>
                          <Image
                            width={200}
                            src={`https://driveshield.net/storage/${followUp.image_path}`}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                          />
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label={t('visits.details.communicationWay')}>
                        {followUp.communication_way}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('visits.details.createdAt')}>
                        {new Date(followUp.created_at).toLocaleString()}
                      </Descriptions.Item>
                      {(hasPermission('add_visit_note') ||
                        hasPermission('update_visit') ||
                        hasPermission('visits.add_note')) && (
                        <Descriptions.Item label={t('visits.details.actions')}>
                          <Button
                            type="primary"
                            size="small"
                            icon={<CommentOutlined />}
                            onClick={() => handleOpenNoteModal(followUp)}
                          >
                            {t('visits.actions.addNote')}
                          </Button>
                        </Descriptions.Item>
                      )}
                    </Descriptions>

                    {/* Admin Notes within Follow-up */}
                    {followUp.admin_note &&
                      Array.isArray(followUp.admin_note) &&
                      followUp.admin_note.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                          <Divider orientation="left" style={{ fontSize: '14px', fontWeight: 600 }}>
                            {t('visits.details.adminNotes')}
                          </Divider>
                          <div className="admin-notes-section">
                            {followUp.admin_note.map((note: any, index: number) => (
                              <div key={index} className="admin-note-item">
                                <div className="admin-note-content">
                                  <Text>
                                    {typeof note === 'string'
                                      ? note
                                      : note.note || note.content || JSON.stringify(note)}
                                  </Text>
                                </div>
                                {note.created_at && (
                                  <div className="admin-note-timestamp">
                                    {new Date(note.created_at).toLocaleString()}
                                  </div>
                                )}
                                {note.created_at === undefined && note.timestamp && (
                                  <div className="admin-note-timestamp">
                                    {new Date(note.timestamp).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </Card>
                ))}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Add Note Modal */}
      <Modal
        title={t('visits.actions.addNote')}
        visible={noteModalVisible}
        onCancel={handleCloseNoteModal}
        footer={[
          <Button key="cancel" onClick={handleCloseNoteModal}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={actionLoading}
            onClick={() => {
              noteForm.validateFields().then((values) => {
                if (selectedFollowUp) {
                  handleAddFollowUpNote(selectedFollowUp.id, values.note);
                }
              });
            }}
          >
            {t('common.save')}
          </Button>,
        ]}
      >
        <Form form={noteForm} layout="vertical">
          <Form.Item
            name="note"
            label={t('visits.details.note')}
            rules={[{ required: true, message: t('visits.validation.noteRequired') }]}
          >
            <AntTextArea rows={4} placeholder={t('visits.details.notePlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>
    </AnimatedContainer>
  );
};

export default Visits;
